import { createFileRoute, useParams, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Loader2, TriangleAlert, Lock, ArrowLeft } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { useAtomValue } from 'jotai'
import { Main } from '@/components/main'
import MainHeader from '@/components/layout/MainHeader'
import { SubmissionDetailsCard } from '@/components/compliance/SubmissionDetailsCard'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { EvidencePack } from '@/components/compliance/EvidencePack'
import { Button } from '@/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/submissions/$submissionId'
)({
  component: SubmissionDetailsPage,
})

function SubmissionDetailsPage() {
  const { submissionId } = useParams({ strict: false }) as { submissionId: string }
  const workspaceId = useAtomValue(workspaceIdAtom)
  const navigate = useNavigate()
  const { canReview } = useWorkspacePermissions(workspaceId || undefined)

  // Fetch submission details
  const submissionQuery = useQuery({
    ...trpc.guidedTemplate.getSubmission.queryOptions({ id: submissionId }),
    enabled: !!submissionId,
  })

  const submission = submissionQuery.data?.submission
  const status = submissionQuery.data?.status

  // Check if this is the currently active submission
  const isActiveSubmission = status?.activeSubmissionId === submissionId

  if (submissionQuery.isLoading) {
    return (
      <Main>
        <MainHeader />
        <div className='flex h-[50vh] w-full items-center justify-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-6 w-6 animate-spin' />
        </div>
      </Main>
    )
  }

  // Check if this is a permission denied error
  const isForbidden = submissionQuery.error?.message?.toLowerCase().includes('permission') ||
    submissionQuery.error?.message?.toLowerCase().includes('forbidden') ||
    submissionQuery.error?.data?.code === 'FORBIDDEN'

  // Extract metadata from FORBIDDEN error for breadcrumb display
  const errorCause = submissionQuery.error?.data?.cause as {
    testId?: string
    testCode?: string
    revisionNumber?: number
  } | undefined
  const errorTestId = errorCause?.testId
  const errorTestCode = errorCause?.testCode
  const errorRevisionNumber = errorCause?.revisionNumber

  if (submissionQuery.error || !submission) {
    return (
      <Main>
        <MainHeader className='!mb-0'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to='/workspaces/$workspaceId/tasks'
                    params={{ workspaceId: workspaceId || '' }}
                  >
                    My Tasks
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {errorTestId && errorTestCode && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        to='/workspaces/$workspaceId/compliance/tests/$testId'
                        params={{
                          workspaceId: workspaceId || '',
                          testId: errorTestId,
                        }}
                      >
                        {errorTestCode}
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {errorRevisionNumber ? `Submission #${errorRevisionNumber}` : 'Submission'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </MainHeader>
        <div className='mx-auto box-content flex max-w-4xl flex-col px-6 pb-12 pt-6'>
          {isForbidden ? (
            // AC-3: Better UI for access denied
            <div className='rounded-xl border overflow-hidden'>
              <div className='px-4 py-3 border-b border-amber-200 dark:border-amber-500 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300'>
                <div className='flex items-center gap-2'>
                  <Lock className='h-4 w-4 flex-shrink-0' />
                  <span className='font-semibold text-sm'>Access Restricted</span>
                </div>
                <p className='text-xs mt-1 opacity-90'>
                  You do not have permission to view this submission.
                </p>
              </div>
              <div className='p-6 space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  This submission belongs to a task that is not assigned to you. You can only view submissions for tasks you are assigned to, or submissions you have created.
                </p>
                <Button
                  variant='outline'
                  onClick={() => navigate({ to: '/workspaces/$workspaceId/tasks', params: { workspaceId: workspaceId || '' } })}
                >
                  <ArrowLeft className='h-4 w-4' />
                  Back to My Tasks
                </Button>
              </div>
            </div>
          ) : (
            // Generic error state
            <div className='flex h-[50vh] w-full flex-col items-center justify-center gap-4'>
              <TriangleAlert className='h-8 w-8 text-muted-foreground' />
              <p className='text-muted-foreground'>{submissionQuery.error?.message || 'Submission not found'}</p>
              <Button
                variant='outline'
                onClick={() => navigate({ to: '/workspaces/$workspaceId/tasks', params: { workspaceId: workspaceId || '' } })}
              >
                <ArrowLeft className='h-4 w-4' />
                Back to My Tasks
              </Button>
            </div>
          )}
        </div>
      </Main>
    )
  }

  return (
    <Main>
      <MainHeader className='!mb-0'>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to='/workspaces/$workspaceId/compliance'
                  params={{ workspaceId: workspaceId || '' }}
                >
                  Tests
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  to='/workspaces/$workspaceId/compliance/tests/$testId'
                  params={{
                    workspaceId: workspaceId || '',
                    testId: status?.id || submission.statusId || '',
                  }}
                >
                  {status?.testCode || 'Test'}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Submission #{submission.revisionNumber || 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </MainHeader>
      <div className='mx-auto box-content flex max-w-4xl flex-col px-6 pb-12 pt-6'>

        {/* //Not active submission notice */}
        {!isActiveSubmission && status?.activeSubmissionId && (
          <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30 mb-6'>
            <p className='text-sm text-amber-800 dark:text-amber-200'>
              This submission has been superseded by a newer version.{' '}
              <Link
                to='/workspaces/$workspaceId/compliance/submissions/$submissionId'
                params={{
                  workspaceId: workspaceId || '',
                  submissionId: status.activeSubmissionId,
                }}
                className='font-medium underline'
              >
                View current submission →
              </Link>
            </p>
          </div>
        )}

        {/* Main card - using reusable component */}
        <SubmissionDetailsCard
          submission={submission}
          isActiveSubmission={isActiveSubmission}
          workspaceId={workspaceId || ''}
          testTitle={status?.testTitle || submission.topicTitle}
          testCode={status?.testCode}
          testId={status?.id || submission.statusId}
          showDetailedScores={canReview}
          showViewButton={false}
        />
        {submission.evidencePack?.id && <EvidencePack evidencePackId={submission.evidencePack?.id} />}
      </div>
    </Main>
  )
}
